import { useCallback, useEffect, useState } from 'react';

import { health as healthApi } from '@/helpers/ApiHelper';
import type { ServiceHealth } from '@/types/health';

const POLL_INTERVAL_MS = 30_000;

export interface UseServiceHealthResult {
  data: ServiceHealth[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useServiceHealth(): UseServiceHealthResult {
  const [data, setData] = useState<ServiceHealth[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await healthApi.listServices();
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      try {
        const result = await healthApi.listServices();
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void run();

    const id = setInterval(() => {
      if (!cancelled) {
        void run();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return { data, isLoading, error, refetch: fetchData };
}
